import DownloadIcon from '@mui/icons-material/Download';
import { IconButton } from '@mui/material';
import * as Sentry from '@sentry/react';
import { CustomCellRendererProps } from 'ag-grid-react';
import { useCallback } from 'react';
import { useLoadingBarState } from '../../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../../components/ToastMessage/ToastMessageProvider';
import { TearSheetsUpload } from '../../../../schemas/BulkTearsheetGeneration.schema';
import { downloadTearsheetZip } from '../../../../services/queries/MaggieTearsheetQueries';
import { getErrorMessage } from '../../../../services/queryHelpers';
import { LoadingId } from '../../../../types';

export function BulkDownloadTearsheetZipRenderer(props: CustomCellRendererProps<TearSheetsUpload>) {
  const onDownload = useBulkDownloadTearsheetZip();
  if (!props.data || props.data.id == null) {
    return null;
  }
  return (
    <IconButton onClick={() => onDownload(props.data?.id)} color='primary' title='download all'>
      <DownloadIcon />
    </IconButton>
  );
}

function useBulkDownloadTearsheetZip() {
  const { pushErrorToast } = useToastMessageState();
  const {
    actions: { startLoading, stopLoading },
  } = useLoadingBarState();
  return useCallback(
    async (uploadId?: number) => {
      if (!uploadId) {
        pushErrorToast({ message: 'Missing upload ID' });
        return;
      }
      try {
        startLoading(LoadingId.downloadTearsheetZip);
        const response = await downloadTearsheetZip(uploadId);
        if (!response.url) {
          pushErrorToast({ message: 'No download URL returned' });
          return;
        }
        window.open(response.url, '_blank', 'noopener,noreferrer');
      } catch (error) {
        pushErrorToast({ message: getErrorMessage(error, 'Failed to download tearsheet zip') });
        Sentry.captureException(error);
      } finally {
        stopLoading(LoadingId.downloadTearsheetZip);
      }
    },
    [pushErrorToast, startLoading, stopLoading]
  );
}
